import '../styles/GlobalDesign.css';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : process.env.REACT_APP_API_URL || '/api';

const AdminResellers = () => {
  const navigate = useNavigate();
  const [resellers, setResellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingCommission, setEditingCommission] = useState(null);
  const [newCommissionRate, setNewCommissionRate] = useState('');
  const [editingReseller, setEditingReseller] = useState(null);
  const [editForm, setEditForm] = useState({ nom: '', email: '', telephone: '', whatsapp: '', password: '' });
  const [adjustingBalance, setAdjustingBalance] = useState(null);
  const [balanceAdjustment, setBalanceAdjustment] = useState({ amount: '', description: '' });
  const [balanceAdjustmentError, setBalanceAdjustmentError] = useState('');
  const [savingBalanceAdjustment, setSavingBalanceAdjustment] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    suspended: 0,
    totalCommissions: 0,
    totalWithdrawn: 0,
    pendingBalance: 0
  });

  useEffect(() => {
    console.log('📍 AdminResellers component mounted');
    console.log(`🌍 Environment: ${process.env.NODE_ENV}`);
    console.log(`🔗 API_URL: ${API_URL}`);
    loadResellers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadResellers = async () => {
    console.log('📍 AdminResellers.loadResellers démarré');
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      console.log(`🔑 Token: ${token ? 'EXISTE (' + token.substring(0, 20) + '...)' : '❌ MANQUANT'}`);
      
      if (!token) {
        console.error('❌ Pas de token trouvé - naviguer vers login');
        setError('Token manquant - reconnexion requise');
        navigate('/admin/login');
        return;
      }

      console.log(`🔗 API_URL trouvé: ${API_URL}`);
      const endpoint = `${API_URL}/admin/resellers`;
      console.log(`📨 Envoi GET à: ${endpoint}`);

      const response = await axios.get(endpoint, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 15000,
        validateStatus: function(status) {
          return status < 500; // Accept any response < 500 so we can handle 4xx
        }
      });

      console.log(`📥 Réponse reçue (Status ${response.status}):`, response.data);
      
      // Vérifier le statut
      if (response.status === 401) {
        console.error('❌ Unauthorized (401) - Token expiré?');
        setError('Session expirée - veuillez vous reconnecter');
        setResellers([]);
        setStats({...stats});
        navigate('/admin/login');
        return;
      }

      if (response.status === 403) {
        console.error('❌ Forbidden (403) - Accès refusé');
        setError('Accès refusé - vous n\'êtes pas administrateur');
        setResellers([]);
        setStats({...stats});
        return;
      }

      if (response.status !== 200) {
        console.error(`❌ Erreur HTTP ${response.status}:`, response.data);
        setError(response.data?.error || `Erreur serveur (${response.status})`);
        setResellers([]);
        setStats({...stats});
        return;
      }

      // Vérifier la structure de la réponse
      const resellersData = response.data.resellers;
      const statsData = response.data.stats;
      
      console.log(`✅ Réponse valide:`);
      console.log(`   - resellersData: ${resellersData?.length || 0} items`);
      console.log(`   - Est un Array? ${Array.isArray(resellersData)}`);
      console.log(`   - statsData: ${statsData ? 'présent' : 'manquant'}`);

      if (!Array.isArray(resellersData)) {
        console.warn('⚠️  resellersData n\'est pas un array, forcé à []');
        setResellers([]);
      } else {
        setResellers(resellersData);
        console.log(`✅ ${resellersData.length} revendeurs définis en state`);
      }

      if (statsData) {
        setStats(statsData);
      }
      
      setError('');
      console.log('✅ Chargement réussi - loading va passer à false');

    } catch (err) {
      console.error('❌ ERREUR lors du chargement:', {
        message: err.message,
        name: err.name,
        code: err.code,
        status: err.response?.status,
        data: err.response?.data
      });

      let errorMsg = 'Erreur lors du chargement des revendeurs';
      
      if (err.message.includes('abort')) {
        errorMsg = 'Timeout - le serveur met trop de temps à répondre';
      } else if (err.message.includes('Network')) {
        errorMsg = 'Erreur réseau - vérifiez votre connexion';
      } else if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      }

      setError(errorMsg);
      setResellers([]);
      console.error(`📌 État d'erreur défini: ${errorMsg}`);

    } finally {
      // TOUJOURS mettre loading à false, peu importe ce qui se passe
      setLoading(false);
      console.log('✅ ENFIN setLoading(false) exécuté');
    }
  };

  const handleToggleStatus = async (resellerId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const newStatus = currentStatus === 'active' ? 'suspended' : 'active';

      await axios.put(
        `${API_URL}/admin/resellers/${resellerId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      loadResellers();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors du changement de statut');
    }
  };

  const handleToggleGenerateCodes = async (resellerId, currentValue) => {
    try {
      const token = localStorage.getItem('token');
      const newValue = currentValue ? 0 : 1;

      await axios.put(
        `${API_URL}/admin/resellers/${resellerId}/generate-codes`,
        { can_generate_codes: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      loadResellers();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  const handleToggleGenerate24hCodes = async (resellerId, currentValue) => {
    try {
      const token = localStorage.getItem('token');
      const newValue = currentValue ? 0 : 1;

      await axios.put(
        `${API_URL}/admin/resellers/${resellerId}/generate-24h-codes`,
        { can_generate_24h_codes: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      loadResellers();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  const handleEditCommission = (reseller) => {
    setEditingCommission(reseller.id);
    setNewCommissionRate(reseller.commission_rate);
  };

  const handleSaveCommission = async (resellerId) => {
    try {
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API_URL}/admin/resellers/${resellerId}/commission`,
        { commission_rate: parseFloat(newCommissionRate) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setEditingCommission(null);
      loadResellers();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la modification');
    }
  };

  const handleCancelEdit = () => {
    setEditingCommission(null);
    setNewCommissionRate('');
  };

  const handleOpenEditReseller = (reseller) => {
    setEditingReseller(reseller.id);
    setEditForm({
      nom: reseller.nom || '',
      email: reseller.email || '',
      telephone: reseller.telephone || '',
      whatsapp: reseller.whatsapp || '',
      password: ''
    });
  };

  const handleSaveEditReseller = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/admin/resellers/${editingReseller}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingReseller(null);
      loadResellers();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la modification');
    }
  };

  const handleOpenBalanceAdjustment = (reseller) => {
    setAdjustingBalance(reseller);
    setBalanceAdjustment({ amount: '', description: '' });
    setBalanceAdjustmentError('');
  };

  const handleSaveBalanceAdjustment = async (event) => {
    event.preventDefault();
    const amount = Number(balanceAdjustment.amount);
    if (!Number.isSafeInteger(amount) || amount <= 0) {
      setBalanceAdjustmentError('Saisissez un montant entier positif en FCFA.');
      return;
    }

    setSavingBalanceAdjustment(true);
    setBalanceAdjustmentError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/admin/resellers/${adjustingBalance.id}/adjust-balance`,
        { amount, description: balanceAdjustment.description.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAdjustingBalance(null);
      await loadResellers();
      alert(`${response.data.message}. Nouveau solde : ${Number(response.data.new_balance).toLocaleString()} FCFA.`);
    } catch (err) {
      setBalanceAdjustmentError(err.response?.data?.error || 'Erreur lors de l’ajout au solde principal.');
    } finally {
      setSavingBalanceAdjustment(false);
    }
  };

  const handleDeleteReseller = async (reseller) => {
    if (!window.confirm(`Supprimer le revendeur "${reseller.nom}" ?\n\nSes clients seront dissociés mais non supprimés.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/admin/resellers/${reseller.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadResellers();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const copyResellerUrl = (slug) => {
    const url = `http://51.254.135.114/reseller/${slug}`;
    navigator.clipboard.writeText(url);
    alert('Lien copié !');
  };

  const filteredResellers = resellers.filter(r =>
    (r.nom || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (r.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (r.slug || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="resellers-page">
          <div className="loading">Chargement...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="resellers-page">
        <div className="page-header">
          <h1>👥 Gestion des Revendeurs</h1>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Stats */}
        <div className="resellers-stats-grid">
          <div className="rs-stat-card blue">
            <div className="rs-stat-icon">👥</div>
            <div>
              <div className="rs-stat-value">{stats.total}</div>
              <div className="rs-stat-label">Total Revendeurs</div>
            </div>
          </div>
          <div className="rs-stat-card green">
            <div className="rs-stat-icon">✅</div>
            <div>
              <div className="rs-stat-value">{stats.active}</div>
              <div className="rs-stat-label">Actifs</div>
            </div>
          </div>
          <div className="rs-stat-card red">
            <div className="rs-stat-icon">⛔</div>
            <div>
              <div className="rs-stat-value">{stats.suspended}</div>
              <div className="rs-stat-label">Suspendus</div>
            </div>
          </div>
          <div className="rs-stat-card gold">
            <div className="rs-stat-icon">💰</div>
            <div>
              <div className="rs-stat-value">{(stats.totalCommissions || 0).toLocaleString()}</div>
              <div className="rs-stat-label">Commissions FCFA</div>
            </div>
          </div>
          <div className="rs-stat-card orange">
            <div className="rs-stat-icon">💸</div>
            <div>
              <div className="rs-stat-value">{(stats.totalWithdrawn || 0).toLocaleString()}</div>
              <div className="rs-stat-label">Retiré FCFA</div>
            </div>
          </div>
          <div className="rs-stat-card cyan">
            <div className="rs-stat-icon">🔄</div>
            <div>
              <div className="rs-stat-value">{(stats.pendingBalance || 0).toLocaleString()}</div>
              <div className="rs-stat-label">Solde Dispo FCFA</div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="resellers-section">
          <h2>Liste des Revendeurs</h2>
          <div className="search-bar" style={{ marginBottom: '16px' }}>
            <input
              type="text"
              placeholder="🔍 Rechercher un revendeur (nom, email, slug)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {resellers.length === 0 ? (
            <div className="empty-state"><p>Aucun revendeur enregistré</p></div>
          ) : filteredResellers.length === 0 ? (
            <div className="empty-state"><p>Aucun résultat pour « {searchTerm} »</p></div>
          ) : (
            <div className="table-container">
              <table className="resellers-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>Clients</th>
                    <th>Solde</th>
                    <th>Commission</th>
                    <th>Total Gagné</th>
                    <th>Codes payants</th>
                    <th>Codes 24h</th>
                    <th>Statut</th>
                    <th>Créé le</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResellers.map((reseller) => (
                    <tr key={reseller.id}>
                      <td>
                        <div className="reseller-name">
                          <strong>{reseller.nom}</strong>
                          <small className="slug">/{reseller.slug}</small>
                        </div>
                      </td>
                      <td><span className="reseller-email" title={reseller.email}>{reseller.email}</span></td>
                      <td className="text-center">
                        <span className="badge badge-info">{reseller.total_clients || 0}</span>
                      </td>
                      <td className="text-right">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                          <strong>{parseFloat(reseller.balance || 0).toLocaleString()} FCFA</strong>
                          <button
                            type="button"
                            onClick={() => handleOpenBalanceAdjustment(reseller)}
                            className="rs-btn rs-btn-edit"
                            title="Ajouter au solde principal"
                            aria-label={`Ajouter au solde principal de ${reseller.nom}`}
                          >+</button>
                        </div>
                      </td>
                      <td>
                        {editingCommission === reseller.id ? (
                          <div className="commission-edit-inline">
                            <input
                              type="number"
                              value={newCommissionRate}
                              onChange={(e) => setNewCommissionRate(e.target.value)}
                              min="0" max="100" step="0.1"
                            />
                            <button onClick={() => handleSaveCommission(reseller.id)} className="btn-ok">✓</button>
                            <button onClick={handleCancelEdit} className="btn-cancel-xs">✕</button>
                          </div>
                        ) : (
                          <div className="commission-cell">
                            <span className="commission-val">{reseller.commission_rate}%</span>
                            <button onClick={() => handleEditCommission(reseller)} className="btn-edit-comm">✏</button>
                          </div>
                        )}
                      </td>
                      <td>{parseFloat(reseller.total_earned || 0).toLocaleString()} FCFA</td>
                      <td className="text-center">
                        <button
                          onClick={() => handleToggleGenerateCodes(reseller.id, reseller.can_generate_codes)}
                          className={`btn-toggle-xs ${reseller.can_generate_codes ? 'enabled' : 'disabled'}`}
                        >
                          {reseller.can_generate_codes ? '✅ ON' : '⛔ OFF'}
                        </button>
                      </td>
                      <td className="text-center">
                        <button
                          onClick={() => handleToggleGenerate24hCodes(reseller.id, reseller.can_generate_24h_codes)}
                          className={`btn-toggle-xs ${reseller.can_generate_24h_codes ? 'enabled' : 'disabled'}`}
                        >
                          {reseller.can_generate_24h_codes ? '⚡ ON' : '⛔ OFF'}
                        </button>
                      </td>
                      <td>
                        <span className={`status-badge status-${reseller.status}`}>
                          {reseller.status === 'active' ? 'Actif' : 'Suspendu'}
                        </span>
                      </td>
                      <td>{new Date(reseller.created_at).toLocaleDateString('fr-FR')}</td>
                      <td>
                        <div className="rs-action-btns">
                          <button onClick={() => copyResellerUrl(reseller.slug)} className="rs-btn rs-btn-copy" title="Copier le lien">🔗</button>
                          <button
                            onClick={() => handleOpenEditReseller(reseller)}
                            className="rs-btn rs-btn-edit"
                            title="Modifier"
                          >✏️</button>
                          <button
                            onClick={() => handleToggleStatus(reseller.id, reseller.status)}
                            className={`rs-btn ${reseller.status === 'active' ? 'rs-btn-suspend' : 'rs-btn-activate'}`}
                            title={reseller.status === 'active' ? 'Suspendre' : 'Activer'}
                          >
                            {reseller.status === 'active' ? '⛔' : '✅'}
                          </button>
                          <button
                            onClick={() => handleDeleteReseller(reseller)}
                            className="rs-btn rs-btn-delete"
                            title="Supprimer"
                          >🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal modification revendeur */}
        {editingReseller && (
          <div className="modal-overlay" onClick={() => setEditingReseller(null)}>
            <div className="modal-box" onClick={(e) => e.stopPropagation()}>
              <h3>✏️ Modifier le revendeur</h3>
              <div className="modal-field">
                <label>Nom</label>
                <input
                  type="text"
                  value={editForm.nom}
                  onChange={(e) => setEditForm({ ...editForm, nom: e.target.value })}
                  placeholder="Nom du revendeur"
                />
              </div>
              <div className="modal-field">
                <label>Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  placeholder="Email"
                />
              </div>
              <div className="modal-field">
                <label>Téléphone</label>
                <input
                  type="text"
                  value={editForm.telephone}
                  onChange={(e) => setEditForm({ ...editForm, telephone: e.target.value })}
                  placeholder="Téléphone"
                />
              </div>
              <div className="modal-field">
                <label>WhatsApp</label>
                <input
                  type="text"
                  value={editForm.whatsapp}
                  onChange={(e) => setEditForm({ ...editForm, whatsapp: e.target.value })}
                  placeholder="WhatsApp"
                />
              </div>
              <div className="modal-field">
                <label>Nouveau mot de passe <small>(laisser vide pour ne pas changer)</small></label>
                <input
                  type="password"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  placeholder="Nouveau mot de passe (min. 6 caractères)"
                />
              </div>
              <div className="modal-actions">
                <button className="btn-save" onClick={handleSaveEditReseller}>💾 Enregistrer</button>
                <button className="btn-cancel" onClick={() => setEditingReseller(null)}>Annuler</button>
              </div>
            </div>
          </div>
        )}

        {adjustingBalance && (
          <div className="modal-overlay" onClick={() => !savingBalanceAdjustment && setAdjustingBalance(null)}>
            <form className="modal-box" onSubmit={handleSaveBalanceAdjustment} onClick={(e) => e.stopPropagation()}>
              <h3>Ajouter au solde principal</h3>
              <p style={{ color: 'var(--gray-400)', margin: '0 0 16px' }}>
                {adjustingBalance.nom} · solde actuel : {Number(adjustingBalance.balance || 0).toLocaleString()} FCFA
              </p>
              <div className="modal-field">
                <label htmlFor="balance-adjustment-amount">Montant à ajouter (FCFA)</label>
                <input
                  id="balance-adjustment-amount"
                  type="number"
                  min="1"
                  step="1"
                  required
                  autoFocus
                  value={balanceAdjustment.amount}
                  onChange={(e) => setBalanceAdjustment({ ...balanceAdjustment, amount: e.target.value })}
                  disabled={savingBalanceAdjustment}
                />
              </div>
              <div className="modal-field">
                <label htmlFor="balance-adjustment-description">Motif <small>(facultatif)</small></label>
                <input
                  id="balance-adjustment-description"
                  type="text"
                  maxLength="255"
                  value={balanceAdjustment.description}
                  onChange={(e) => setBalanceAdjustment({ ...balanceAdjustment, description: e.target.value })}
                  disabled={savingBalanceAdjustment}
                />
              </div>
              {balanceAdjustmentError && <div className="error-message" role="alert">{balanceAdjustmentError}</div>}
              <div className="modal-actions">
                <button className="btn-save" type="submit" disabled={savingBalanceAdjustment}>
                  {savingBalanceAdjustment ? 'Ajout...' : 'Confirmer l’ajout'}
                </button>
                <button className="btn-cancel" type="button" onClick={() => setAdjustingBalance(null)} disabled={savingBalanceAdjustment}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminResellers;
